<template>
  <Modal
    class="scoped"
    :modal-style="{ maxWidth: modalMaxWidth }"
    title="Import Content"
  >
    <QCard class="full-width">
      <QCardSection class="preview-body scroll">
        <div style="max-width: 100vw">
          <QCardSection class="row items-center">
            <h4>Import Content via CSV</h4>
          </QCardSection>
          <div
            v-if="!reviewingItems"
            class="row q-pa-md q-gutter-md"
          >
            <QInput
              accept=".csv"
              class="col-11"
              dense
              filled
              hint="Upload CSV"
              square
              type="file"
              @update:model-value="processCsvFile"
            />
          </div>
          <div
            v-if="reviewingItems"
            class="row q-pa-md q-gutter-md"
          >
            <QForm
              v-if="coverageToUpload.length"
              ref="form"
              class="full-width"
            >
              <QMarkupTable
                class="full-width import-table"
                style="height: 600px"
              >
                <thead>
                  <tr>
                    <th class="text-left">Title</th>
                    <th class="text-left">URL</th>
                    <th class="text-left">Published At</th>
                    <th class="text-left">Time Zone</th>
                    <th class="text-left">Medium</th>
                    <th class="text-left">Source Name</th>
                    <th class="text-left">Author</th>
                    <th class="text-left">Audience</th>
                    <th class="text-left">Advertisement Rate</th>
                    <th class="text-left">Advertisement Rate Currency</th>
                    <th class="text-left">Summary</th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="(item, index) in coverageToUpload"
                    :key="index"
                  >
                    <td class="text-left">
                      <QInput
                        v-model="item.title"
                        :class="{ 'q-pa-none': item.error }"
                        dense
                        filled
                        label="Title"
                        :rules="[
                          (val) =>
                            (val && val.length > 0) || 'Must have a Title',
                        ]"
                        square
                      />
                    </td>
                    <td class="text-left">
                      <QInput
                        v-model="item.url"
                        :class="{ 'q-pa-none': item.error }"
                        dense
                        filled
                        label="URL"
                        :rules="[
                          (val) =>
                            isValidOptionalUrl(val) || 'Must have a valid URL',
                        ]"
                        square
                      />
                    </td>
                    <td class="text-left">
                      <TimezoneRangePicker
                        v-model="item.published_at"
                        date-style="medium"
                        hide-button
                        hide-timezone
                        show-input
                        size="sm"
                        :timezone="item.time_zone"
                        type="day"
                      />
                    </td>
                    <td class="text-left">
                      <TimezoneDropdown
                        v-model="item.time_zone"
                        dense
                        filled
                        :rules="[
                          (val) =>
                            (val && val.length > 0) ||
                            'Must have a valid Timezone',
                        ]"
                      />
                    </td>
                    <td class="text-left">
                      <SelectList
                        v-model="item.medium"
                        dense
                        filled
                        label="Medium"
                        :options="mediaOptions"
                        :rules="[
                          (val) =>
                            (val && val.length > 0) || 'Must have a Medium',
                        ]"
                        square
                      />
                    </td>
                    <td class="text-left">
                      <QInput
                        v-model="item.source_name"
                        dense
                        filled
                        label="Source Name"
                        square
                      />
                    </td>
                    <td class="text-left">
                      <QInput
                        v-model="item.author"
                        dense
                        filled
                        label="Author"
                        square
                      />
                    </td>
                    <td class="text-left">
                      <QInput
                        v-model="item.audience"
                        :class="{ 'q-pa-none': item.error }"
                        dense
                        filled
                        label="Audience"
                        :rules="[
                          (val) =>
                            isValidOptionalPositiveNumber(val) ||
                            'Audience must be a positive number',
                        ]"
                        square
                      />
                    </td>
                    <td class="text-left">
                      <QInput
                        v-model="item.advertisement_rate"
                        :class="{ 'q-pa-none': item.error }"
                        dense
                        filled
                        label="Advertisement Rate"
                        :rules="[
                          (val) =>
                            isValidOptionalPositiveNumber(val) ||
                            'Advertisement Rate must be a positive number',
                        ]"
                        square
                      />
                    </td>
                    <td class="text-left">
                      <SelectList
                        v-model="item.advertisement_rate_currency"
                        :class="{ 'q-pa-none': item.error }"
                        clearable
                        dense
                        filled
                        label="Advertisement Rate Currency"
                        :options="currencyOptions"
                        :rules="[(val) => isValidOptionalCurrency(val, item)]"
                        square
                      />
                    </td>
                    <td class="text-left">
                      <QInput
                        v-model="item.summary"
                        dense
                        filled
                        label="Summary"
                        square
                      />
                    </td>
                  </tr>
                </tbody>
              </QMarkupTable>
            </QForm>
          </div>
        </div>
      </QCardSection>

      <QCardSection
        v-if="!reviewingItems"
        align="right"
      >
        <VueJsonCsv
          class="col"
          :data="sample"
          name="sample.csv"
        >
          <QBtn
            color="primary"
            flat
            label="Sample.csv"
            no-caps
          />
        </VueJsonCsv>
      </QCardSection>

      <QCardActions>
        <div class="full-width flex justify-between">
          <div>
            <QBtn
              v-if="reviewingItems"
              color="primary"
              flat
              label="Back"
              no-caps
              @click="reviewingItems = false"
            />
          </div>
          <div>
            <div>
              <QBtn
                v-if="!reviewingItems"
                color="black"
                label="Review Coverage"
                no-caps
                @click="reviewCoverage"
              />
              <QBtn
                v-if="reviewingItems"
                v-close-popup
                color="black"
                label="Import Coverage"
                no-caps
                @click="importCoverage"
              />
            </div>
          </div>
        </div>
      </QCardActions>
    </QCard>
  </Modal>
</template>

<script>
import { keyBy } from "lodash-es";
import Papa from "papaparse";
import VueJsonCsv from "vue-json-csv";

import Modal from "shared/components/core/modals/Modal.vue";
import TimezoneDropdown from "shared/components/core/pickers/TimezoneDropdown.vue";
import TimezoneRangePicker from "shared/components/core/pickers/TimezoneRangePicker.vue";
import { isUrl } from "shared/composables/useValidation";
import { enabledCurrencies } from "shared/constants/currencies";
import { parseDate } from "shared/helpers/date";
import { isNumber } from "shared/helpers/number";

import SelectList from "src/components/common/SelectList.vue";

const TIME_ZONES = keyBy(Intl.supportedValuesOf("timeZone"), (timezone) =>
  timezone.toLowerCase()
);

export default {
  name: "UploadCoverageCsvModal",
  components: {
    Modal,
    VueJsonCsv,
    TimezoneRangePicker,
    TimezoneDropdown,
    SelectList,
  },
  props: {
    organisationId: {
      type: [Number, String],
      required: true,
    },
    userId: {
      type: [Number, String],
      required: true,
    },
    insightsQueryId: {
      type: [Number, String],
      required: true,
    },
    enabledMedia: {
      type: Array,
      required: true,
    },
  },
  emits: ["confirm"],
  data() {
    return {
      reviewingItems: false,
      coverageToUpload: [],
    };
  },
  computed: {
    sample() {
      return [
        {
          title: "Article Title",
          url: "http://article.url.com/article - optional",
          published_at: "12/25/2024 | 12/25/2024, 12:00:00 AM",
          time_zone: "Australia/Sydney",
          medium: this.enabledMedia.join("|"),
          source_name: "Article Source Name - optional",
          author: "Article Author - optional",
          audience: "12345 - optional",
          advertisement_rate: "12345 - optional",
          advertisement_rate_currency: "AUD,USD,etc - optional",
          summary: "Article Summary - optional",
        },
      ];
    },
    modalMaxWidth() {
      return this.reviewingItems ? "100%" : "500px";
    },
    currencyOptions() {
      return enabledCurrencies.map((currency) => ({
        id: currency.code,
        label: `${currency.code} | ${currency.name}`,
      }));
    },
    mediaOptions() {
      return this.enabledMedia.map((media) => ({
        id: media,
        label: media,
      }));
    },
  },
  methods: {
    processCsvFile([file]) {
      const reader = new FileReader();

      reader.onload = (event) => {
        const csvFile = event.target.result.trim();
        const parsed = Papa.parse(csvFile).data;
        const columns = parsed.shift();

        this.coverageToUpload = parsed.map((values) => {
          // set columns uploaded
          const item = values.reduce((acc, value, index) => {
            switch (columns[index]) {
              case "published_at":
                acc[columns[index]] = parseDate(value);
                break;
              case "time_zone":
                acc[columns[index]] = this.parseTimezone(value);
                break;
              case "medium":
                acc[columns[index]] = this.parseMedium(value);
                break;
              case "advertisement_rate_currency":
                acc[columns[index]] = this.parseCurrency(value);
                break;
              default:
                acc[columns[index]] = value;
            }

            return acc;
          }, {});

          return item;
        });
      };

      reader.readAsText(file);
    },
    async importCoverage() {
      try {
        this.$q.loading.show();

        const valid = await this.$refs.form.validate();

        if (!valid) {
          this.$dialog.alert({
            title: "Invalid Content",
            message: "Please correct errors and try again.",
          });

          return false;
        }

        await this.$streemApiAdmin.post("external_items/import", {
          params: {
            external_items: this.coverageToUpload,
          },
        });

        this.$emit("confirm");
      } catch (error) {
        this.$error("Unable to import coverage", error);
      } finally {
        this.$q.loading.hide();
      }

      return true;
    },
    async reviewCoverage() {
      this.applyPropertiesToCoverage();

      try {
        const response = (
          await this.$streemApiAdmin.post("external_items/import", {
            params: {
              validation_only: true,
              external_items: this.coverageToUpload,
            },
          })
        ).data;

        this.coverageToUpload = response.map((item) => ({
          ...item,
          published_at: parseDate(item.published_at),
        }));

        this.reviewingItems = true;
      } catch (error) {
        this.$error("Unable to validate coverage", error);
      }
    },
    applyPropertiesToCoverage() {
      this.coverageToUpload = this.coverageToUpload.map((item) => ({
        ...item,
        organisation_id: this.organisationId,
        user_id: this.userId,
        insights_query_ids: [this.insightsQueryId],
      }));
    },
    isValidOptionalUrl(val) {
      return !val || isUrl(val);
    },
    isValidOptionalPositiveNumber(val) {
      return !val || (isNumber(val) && val >= 0);
    },
    isValidOptionalCurrency(val, item) {
      if (item.advertisement_rate && Boolean(val)) return true;
      if (!item.advertisement_rate && !val) return true;

      return "Must have a valid Currency with Ad Rate";
    },
    parseTimezone(timezone) {
      return TIME_ZONES[timezone.toLowerCase()];
    },
    parseMedium(medium) {
      return this.enabledMedia.find(
        (enabledMedium) => enabledMedium.toLowerCase() === medium.toLowerCase()
      );
    },
    parseCurrency(currency) {
      return this.currencyOptions.find(
        (currencyOption) =>
          currencyOption.id.toLowerCase() === currency.toLowerCase()
      );
    },
  },
};
</script>

<style lang="scss" scoped>
:deep() {
  .import-table {
    .q-field {
      width: 200px;
    }

    .q-field.old-design {
      width: 300px;
    }
  }
}
</style>
